/**
 * Tag Editor Inline Edit Service
 * Orchestrates inline tag editing functionality
 */

import { Injectable, NgZone } from '@angular/core'
import {
  COMPONENT_STRINGS,
  CSS_CLASSES,
  HTML_ATTRIBUTES,
  HTML_VALUES,
  TAG_EDITOR_COLORS,
  TAG_EDITOR_DELAY,
  TAG_EDITOR_STRINGS,
} from '../tag-editor.constants'
import { EditorInteractionService } from './editor-interaction.service'
import { TagEditorStateService } from './tag-editor-state.service'
import { DebugLoggingService } from './debug-logging.service'
import { InlineEditingState } from '../tag-editor.model'

/**
 * Callbacks for inline editing operations
 */
export interface InlineEditCallbacks {
  readonly onEditStart: (tagElement: HTMLElement) => void
  readonly onEditEnd: (tagElement: HTMLElement, save: boolean) => void
  readonly onContentUpdate: (content: string) => void
  readonly onStateUpdate: () => void
  readonly getEditorHTML: () => string
  readonly onTagsParse: () => void
  readonly onEmitChange: (content: string) => void
  readonly onPendingContentSet: (content: string) => void
}

@Injectable({ providedIn: 'root' })
export class TagEditorInlineEditService {
  constructor(
    private readonly ngZone: NgZone,
    private readonly editorInteractionService: EditorInteractionService,
    private readonly stateService: TagEditorStateService,
    private readonly debugLoggingService: DebugLoggingService,
  ) {}

  private debugLog(message: string, ...args: ReadonlyArray<unknown>): void {
    this.debugLoggingService.logWithService(COMPONENT_STRINGS.COMPONENT_NAME, message, ...args)
  }

  /**
   * Enters inline edit mode for a tag
   */
  public enterEditMode(tagElement: HTMLElement, callbacks: InlineEditCallbacks, clickRange?: Range): void {
    const tagId: string | null = tagElement.getAttribute(HTML_ATTRIBUTES.DATA_TAG_ID)
    if (!tagId) {
      return
    }

    const editingState: InlineEditingState = {
      element: tagElement,
      originalText: tagElement.textContent || TAG_EDITOR_STRINGS.EMPTY_STRING,
      tagId,
    }

    tagElement.setAttribute(HTML_ATTRIBUTES.CONTENTEDITABLE, HTML_VALUES.CONTENTEDITABLE_TRUE)
    tagElement.classList.add(CSS_CLASSES.TAG_EDITING)
    tagElement.style.setProperty(
      TAG_EDITOR_STRINGS.CARET_COLOR_PROPERTY,
      TAG_EDITOR_COLORS.CARET_COLOR,
      TAG_EDITOR_STRINGS.CSS_IMPORTANT,
    )

    tagElement.focus()

    requestAnimationFrame(() => {
      const selection: Selection | null = window.getSelection()
      if (!selection) {
        return
      }

      if (clickRange && tagElement.contains(clickRange.startContainer)) {
        try {
          selection.removeAllRanges()
          selection.addRange(clickRange)
          return
        } catch (error: unknown) {
          this.debugLog('enterInlineEditMode: Could not use click range:', error)
        }
      }

      const range: Range = document.createRange()
      range.selectNodeContents(tagElement)
      range.collapse(false)
      selection.removeAllRanges()
      selection.addRange(range)
    })

    this.editorInteractionService.startPreservingCursor(tagElement)

    const inputListener = (): void => {
      this.ngZone.runOutsideAngular(() => {
        const editingTag = this.stateService.getEditingTag()
        if (editingTag) {
          const currentContent = callbacks.getEditorHTML()
          callbacks.onContentUpdate(currentContent)
          callbacks.onPendingContentSet(currentContent)
        }
      })
    }
    tagElement.addEventListener('input', inputListener)

    const blurListener = (): void => {
      setTimeout(() => {
        const currentEditingTag: InlineEditingState | null = this.stateService.getEditingTag()
        if (currentEditingTag?.element === tagElement) {
          callbacks.onEditEnd(tagElement, true)
        }
      }, TAG_EDITOR_DELAY.EXIT_EDIT_DELAY_MS)
    }
    tagElement.addEventListener('blur', blurListener, { once: true })

    this.stateService.setEditingTag({
      ...editingState,
      inputListener,
      blurListener,
    })

    callbacks.onEditStart(tagElement)
  }

  /**
   * Exits inline edit mode and optionally saves changes
   */
  public exitEditMode(save: boolean, callbacks: InlineEditCallbacks): void {
    const editingTag = this.stateService.getEditingTag()
    if (!editingTag) {
      return
    }

    this.debugLog(' Exiting inline edit mode:', { save })

    const { element, originalText } = editingTag

    this.editorInteractionService.stopPreservingCursor(element)

    if (editingTag.inputListener) {
      element.removeEventListener('input', editingTag.inputListener)
    }

    if (editingTag.blurListener) {
      element.removeEventListener('blur', editingTag.blurListener)
    }

    if (!save) {
      element.textContent = originalText
    }

    element.setAttribute(HTML_ATTRIBUTES.CONTENTEDITABLE, HTML_VALUES.CONTENTEDITABLE_FALSE)
    element.classList.remove(CSS_CLASSES.TAG_EDITING)
    element.style.removeProperty(TAG_EDITOR_STRINGS.CARET_COLOR_PROPERTY)

    this.stateService.setEditingTag(null)

    if (save) {
      callbacks.onTagsParse()
      const currentContent: string = callbacks.getEditorHTML()
      const pendingContent: string | null = this.stateService.getPendingContent()
      const contentToEmit: string = currentContent || pendingContent || ''

      this.debugLog(' exitInlineEditMode - emitting changes:', {
        hasPendingContent: !!pendingContent,
        currentContentLength: currentContent.length,
        contentToEmitLength: contentToEmit.length,
      })

      callbacks.onEmitChange(contentToEmit)
      this.stateService.setPendingContent(null)
    }

    callbacks.onEditEnd(element, save)
  }

  /**
   * Ensures all pending changes are saved
   */
  public ensureChangesAreSaved(callbacks: InlineEditCallbacks): void {
    const editingTag = this.stateService.getEditingTag()
    if (editingTag) {
      this.debugLog(' ensureChangesAreSaved: Exiting edit mode and saving changes')
      this.exitEditMode(true, callbacks)
    }
  }
}
